import {
  CalendarClock,
  Check,
  ExternalLink,
  SkipForward,
  Trash2,
  X,
} from 'lucide-react-native';
import { ComponentType } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';

/**
 * Context menu for a task row (Wave 48). Opened by long-press (native)
 * or right-click (web) on any task row. Bottom sheet on mobile,
 * centered card on desktop — same responsive treatment as the pickers.
 *
 * Delete comes in two strengths:
 *   - "Delete" = soft delete (hidden from lists, history preserved,
 *     undoable via the snackbar).
 *   - "Delete forever" = hard delete of the task AND its completion
 *     history — the caller must confirm loudly before executing.
 */

export interface TaskMenuTarget {
  id: string;
  title: string;
}

interface Props {
  task: TaskMenuTarget | null;
  onClose: () => void;
  onOpen?: () => void;
  onComplete?: () => void;
  onSkip?: () => void;
  onReschedule?: () => void;
  onDelete: () => void;
  onDeleteForever: () => void;
}

export function TaskActionsSheet({
  task,
  onClose,
  onOpen,
  onComplete,
  onSkip,
  onReschedule,
  onDelete,
  onDeleteForever,
}: Props) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const centered = width >= 768;

  if (!task) return null;

  const Row = ({
    Icon,
    label,
    sub,
    tint = Palette.ink,
    iconTint = Palette.forest,
    onPress,
    last,
  }: {
    Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
    label: string;
    sub?: string;
    tint?: string;
    iconTint?: string;
    onPress?: () => void;
    last?: boolean;
  }) => (
    <Pressable
      onPress={() => {
        onClose();
        // Let the sheet dismiss before the action runs (some actions
        // open their own modal — stacking them on iOS misbehaves).
        setTimeout(() => onPress?.(), 120);
      }}
      style={({ pressed, hovered }) => [
        styles.row,
        !last && styles.rowBorder,
        (pressed || hovered) && { backgroundColor: '#EEF3EE' },
      ]}>
      <View style={styles.rowIcon}>
        <Icon size={16} color={iconTint} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[styles.rowLabel, { color: tint }]}>{label}</Text>
        {sub ? <Text style={styles.rowSub}>{sub}</Text> : null}
      </View>
    </Pressable>
  );

  return (
    <Modal animationType="fade" transparent visible onRequestClose={onClose}>
      <Pressable
        style={[styles.backdrop, centered && styles.backdropCentered]}
        onPress={onClose}>
        <Pressable
          style={[
            styles.sheet,
            centered
              ? styles.sheetCentered
              : { paddingBottom: Math.max(24, insets.bottom + 16) },
          ]}
          onPress={(e) => e.stopPropagation()}>
          {!centered ? <View style={styles.handle} /> : null}
          <View style={styles.head}>
            <Text style={styles.title} numberOfLines={2}>
              {task.title}
            </Text>
            <Pressable
              onPress={onClose}
              hitSlop={10}
              accessibilityLabel="Close"
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.7 }]}>
              <X size={16} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.card}>
            {onOpen ? (
              <Row Icon={ExternalLink} label="Open details" onPress={onOpen} />
            ) : null}
            {onComplete ? (
              <Row Icon={Check} label="Mark done" onPress={onComplete} />
            ) : null}
            {onSkip ? (
              <Row
                Icon={SkipForward}
                label="Skip"
                iconTint={Palette.clay}
                onPress={onSkip}
              />
            ) : null}
            {onReschedule ? (
              <Row
                Icon={CalendarClock}
                label="Reschedule…"
                iconTint={Palette.honeyDeep}
                onPress={onReschedule}
              />
            ) : null}
            <Row
              Icon={Trash2}
              label="Delete"
              sub="Hidden from your plan — history stays, undoable."
              tint={Palette.clay}
              iconTint={Palette.clay}
              onPress={onDelete}
            />
            <Row
              Icon={Trash2}
              label="Delete forever"
              sub="Removes the task AND its completion history. No undo."
              tint={Palette.clay}
              iconTint={Palette.clay}
              onPress={onDeleteForever}
              last
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(42,37,31,0.45)',
    justifyContent: 'flex-end',
  },
  backdropCentered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  sheet: {
    backgroundColor: Palette.sand,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: Spacing.four,
    paddingTop: 12,
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
  },
  sheetCentered: {
    borderRadius: 24,
    maxWidth: 400,
    paddingTop: 18,
    paddingBottom: 18,
  },
  handle: {
    alignSelf: 'center',
    width: 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: Palette.sandDeep,
    marginBottom: 12,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: Spacing.three,
  },
  title: {
    flex: 1,
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    letterSpacing: -0.3,
    lineHeight: 23,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  rowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  rowIcon: {
    width: 30,
    height: 30,
    borderRadius: 10,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
  },
  rowSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 15,
  },
});
